import {
  Middleware,
} from 'redux'
import apiMiddleware from 'middlewares/apiMiddleware'
import rootReducer from './reducers'
import { History, LocationState } from 'history'
import { configureStore } from '@reduxjs/toolkit'
import config from 'constants/config'
import { mapObject } from 'lib/object/objectUtils'

// Replace the packages array with a string, because our insanely huge package arrays badly affect the performance of Redux DevTools
function stubOfferPackages(offer: App.Offer | undefined) {
  if (Array.isArray(offer?.packages)) {
    return {
      ...offer,
      packages: `[${offer.packages.length} packages hidden. Disable SANITISE_REDUX_OFFERS to see them here.]`,
    }
  }
  return offer
}

const actionSanitisers: Array<{ enabled: boolean, sanitise: (action: any, idNumber: number) => any }> = [
  {
    enabled: true,
    sanitise: (action) => {
      if (action.api) {
        return {
          ...action,
          type: `${action.api} - ${action.type.replace('API_CALL_', '')}`,
        }
      }
      return action
    },
  }, {
    enabled: !!config.SANITISE_REDUX_OFFERS,
    sanitise: (action) => {
      if (action.type === 'api/FETCH_OFFER - SUCCESS') {
        return {
          ...action,
          data: stubOfferPackages(action.data),
        }
      }
      if (action.type === 'api/FETCH_OFFERS - SUCCESS') {
        return {
          ...action,
          data: action.data.map((offer: App.Offer) => stubOfferPackages(offer)),
        }
      }
      return action
    },
  },
]

const stateSanitisers: Array<{ enabled: boolean, sanitise: (state: any, index: number) => any }> = [
  {
    enabled: !!config.SANITISE_REDUX_OFFERS,
    sanitise: (state) => {
      if (state?.offer?.offers && Object.values(state.offer.offers).some((offer: any) => Array.isArray(offer?.packages))) {
        return {
          ...state,
          offer: {
            ...state.offer,
            offers: mapObject(state.offer.offers, stubOfferPackages),
          },
        }
      }
      return state
    },
  },
]

interface StoreOptions {
  apiMiddleware?: boolean;
  devTools?: boolean;
}

export default function buildStore<S>(
  history: History<LocationState>,
  initialState?: Partial<S>,
  middlewares: Array<Middleware<S>> = [],
  options: StoreOptions = {
    apiMiddleware: true,
    devTools: true,
  },
) {
  return configureStore({
    reducer: rootReducer(history),
    middleware: (defaultMiddleware) => defaultMiddleware({
      serializableCheck: false,
      immutableCheck: config.REDUX_IMMUTABILITY_CHECK_ENABLED,
    }).concat([
      ...(options.apiMiddleware ? [apiMiddleware] : []),
      ...middlewares,
    ]),
    devTools: options.devTools ? {
      autoPause: true,
      actionSanitizer: (action, idNumber) => actionSanitisers.reduce((action, sanitiser) => (
        sanitiser.enabled ? sanitiser.sanitise(action, idNumber) : action
      ), action),
      stateSanitizer: (state: any, index: number) => stateSanitisers.reduce((state, sanitiser) => (
        sanitiser.enabled ? sanitiser.sanitise(state, index) : state
      ), state),
    } : false,
    preloadedState: initialState,
  })
}

export type AppStore = ReturnType<typeof buildStore>
export type AppDispatch = AppStore['dispatch']
